import { useEffect, useState } from "react";
import { ArrowLeft, ArrowUpRight, Calendar, MapPin, RefreshCw, Trophy, Users } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";

import Layout from "../../components/Layout";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";

export default function GestioneRisultati() {
  const navigate = useNavigate();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (manual = false) => {
    if (manual) setRefreshing(true); else setLoading(true);
    try {
      const { data } = await api.get("/results");
      setResults(data);
      if (manual) toast.success("Lista aggiornata");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <Layout>
      <Link to="/admin" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900 mb-6" data-testid="back-to-admin">
        <ArrowLeft className="h-4 w-4" /> Torna al pannello
      </Link>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.25em] text-blue-700 font-bold">Amministrazione</p>
          <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight mt-2">Gestione Risultati</h1>
          <p className="text-slate-500 mt-2">Seleziona una competizione per vedere i voti di ogni giudice per ogni atleta.</p>
        </div>
        <Button onClick={() => load(true)} disabled={refreshing} className="bg-blue-700 hover:bg-blue-800 text-white h-11 px-5" data-testid="refresh-results-button">
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} /> Aggiorna
        </Button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-slate-500 text-sm">Caricamento…</div>
      ) : results.length === 0 ? (
        <div className="bg-white rounded-2xl ring-1 ring-slate-200 p-10 text-center text-slate-500 text-sm">
          Nessuna competizione inviata. I risultati appariranno qui appena un responsabile invia una competizione.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {results.map((c) => (
            <button
              key={c.id}
              onClick={() => navigate(`/admin/risultati/${c.id}`)}
              className="group bg-white text-left rounded-2xl ring-1 ring-slate-200 hover:ring-blue-300 hover:shadow-md hover:-translate-y-0.5 transition-all p-6 flex flex-col gap-3"
              data-testid={`result-card-${c.id}`}
            >
              <div className="flex items-start justify-between">
                <div className="h-10 w-10 rounded-lg bg-amber-50 text-amber-700 flex items-center justify-center">
                  <Trophy className="h-5 w-5" />
                </div>
                <ArrowUpRight className="h-5 w-5 text-slate-300 group-hover:text-slate-700 transition" />
              </div>
              <h3 className="font-display text-lg font-semibold text-slate-900">{c.name}</h3>
              <div className="text-sm text-slate-600 flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" /> {c.location}</div>
              <div className="text-sm text-slate-600 flex items-center gap-2"><Calendar className="h-4 w-4 text-slate-400" /> {new Date(c.date).toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" })}</div>
              <div className="flex items-center justify-between mt-1">
                <span className="text-xs text-slate-500 inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> {c.athletes?.length || 0} atleti · {c.judges?.length || 0} giudici</span>
                <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">Vedi voti</Badge>
              </div>
            </button>
          ))}
        </div>
      )}
    </Layout>
  );
}
